/** TWILIO_LIMITS_ROLES control: show the service member/channel limits + the channel-user role's
 *  editAnyMessageAttributes permission (live), and set them via the execute endpoint (limits → 1000,
 *  grant the permission).
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem, TwilioLimitsDetail } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

function Row({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {ok ? (
        <CheckCircleIcon fontSize="small" color="success" />
      ) : (
        <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
      )}
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

export function TwilioLimitsControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const seed = item.detail as unknown as TwilioLimitsDetail;
  const query = useQuery({
    queryKey: ['preflight', 'twilioLimitsStatus', topId, item.id],
    queryFn: () => items.fetchTwilioLimitsStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;

  // When the live probe confirms the limits, the backend marks the item completed; refetch the
  // list so the row reflects it (guarded on item.status so it runs once).
  useEffect(() => {
    if (status?.ok && status.detail?.limits_ok && item.status !== 'completed') {
      onChanged();
    }
  }, [status, item.status, onChanged]);

  const configure = useMutation({
    mutationFn: () => items.execute(topId, item.id),
    onSuccess: (res) => {
      const run = res.run;
      if (run && run.status === 'error') {
        snackbar.show(run.error_message || 'Could not set limits & permissions.', 'error');
      } else {
        snackbar.show(run?.summary || 'Limits & permissions set.', 'success');
      }
      void query.refetch();
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not set limits & permissions.', 'error'),
  });

  if (status?.reason === 'not_configured' || detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Twilio isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }

  const target = detail?.target_limit ?? 1000;
  const limitsOk = detail?.limits_ok === true;
  const editOk = detail?.edit_attrs_ok === true;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {detail?.error && (
          <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {detail.error}
          </Alert>
        )}
        <Row
          ok={limitsOk}
          label={
            `Member/channel limits = ${target}` +
            (query.isFetching || detail?.limits_channel_members == null
              ? ''
              : ` (members ${detail.limits_channel_members}, channels ${detail.limits_user_channels})`)
          }
        />
        <Row ok={editOk} label="channel-user role has editAnyMessageAttributes" />

        {canManage && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => configure.mutate()}
              disabled={configure.isPending}
              startIcon={configure.isPending ? <CircularProgress size={14} /> : undefined}
            >
              {limitsOk && editOk ? 'Re-apply limits & permissions' : 'Set limits & permissions'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              Refresh
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
